import { orgAPI } from "@/api/api";
import type {
  ExecuteToolboxResponse,
  IntegrationConnection,
} from "@cohesive/api-client";
import type { ZoomRecordingEntity } from "@cohesive/api-entities";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";

interface UseZoomListRecordingsProps {
  connection: IntegrationConnection;
  dateRange?: { from: string; to: string };
  options?: Partial<
    UseInfiniteQueryOptions<
      ExecuteToolboxResponse<
        ZoomRecordingEntity[],
        { next_page_token: string }
      >,
      Error,
      InfiniteData<
        ExecuteToolboxResponse<
          ZoomRecordingEntity[],
          { next_page_token: string }
        >
      >
    >
  >;
}

// Helper functions
function calculateDateDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split("T")[0]; // YYYY-MM-DD
}

function formatToday(): string {
  return new Date().toISOString().split("T")[0];
}

export function useZoomListRecordings({
  connection,
  dateRange,
  options,
}: UseZoomListRecordingsProps) {
  const defaultFrom = calculateDateDaysAgo(30);
  const defaultTo = formatToday();

  return useInfiniteQuery({
    queryKey: [
      "zoom-recordings",
      connection?.id,
      dateRange?.from,
      dateRange?.to,
    ],
    queryFn: async ({ pageParam }: any) => {
      const input: Record<string, unknown> = {
        page_size: 50,
        from: dateRange?.from || defaultFrom,
        to: dateRange?.to || defaultTo,
        ...(pageParam || {}),
      };

      const response = await orgAPI.toolbox.execute<
        ZoomRecordingEntity[],
        Record<string, unknown>,
        { next_page_token: string }
      >({
        integration_connection_id: connection.id,
        action_name: "zoom-meeting-recordings-list",
        action_input: input,
        // Typed entities drive the rich row UI; their generic projections ride
        // along in data_generic and are what get placed on the canvas.
        entity_format: "both",
      });

      return response;
    },
    initialPageParam: {},
    getNextPageParam: (lastPage) => {
      // If has next_page_token, continue in current date range
      if (lastPage.pagination?.next) {
        return {
          next_page_token: lastPage.pagination.next.next_page_token,
        };
      }
      return undefined;
    },
    enabled: !!connection?.integration_id,
    staleTime: 1000 * 60,
    ...options,
  });
}
